import {
  Box,
  FieldSelect,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import {
  FlowMessengerNodeContactForm,
  FlowMessengerNodeContactFormPayload,
} from 'components/settings/components/sections/dixaMessenger/views/flow/flow.types';
import { QueuePicker, QueuePickerOption } from 'components/shared/queuePicker';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';

const QUEUE_FIELD = 'queue';
const EMAIL_FIELD = 'fromEmail';

type StateProps = ReturnType<typeof mapStateToProps>;

type Props = FlowManagerEditModalComponentT<FlowMessengerNodeContactForm> &
  StateProps;

type Errors = {
  [Key in typeof QUEUE_FIELD]?: string;
};

const ContactFormModalImpl = ({
  fromEmails,
  node,
  nodeLabel,
  replaceNode,
  triggerClose,
}: Props): JSX.Element => {
  const { payload } = node;
  const [errors, setErrors] = useState<Errors>({});
  const [{ queue, fromEmail }, setState] =
    useState<FlowMessengerNodeContactFormPayload>({
      queue: null,
      ...payload,
    });

  const isChanged = useIsChanged(
    [payload?.queue, queue],
    [payload?.fromEmail, fromEmail],
  );

  const validate = useCallback(() => {
    const errors: Errors = {};

    if (!queue?.value) {
      errors[QUEUE_FIELD] = 'Selecting a queue is required';
    }

    setErrors(errors);

    return Object.keys(errors).length === 0;
  }, [queue]);

  const handleQueueChange = useCallback((queue?: QueuePickerOption): void => {
    if (!queue) {
      return;
    }

    setState((state) => ({
      ...state,
      queue: {
        value: queue.id,
        label: queue.name,
      },
    }));
  }, []);

  const handleEmailChange = useCallback((emailAddress: string): void => {
    setState((state) => ({
      ...state,
      fromEmail: emailAddress,
    }));
  }, []);

  const handleSaveClick = useCallback(() => {
    if (!validate()) {
      return;
    }

    const nodeOptions: FlowMessengerNodeContactForm = {
      ...node,
      badges: [],
      childIds: [],
      icon: '',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.CONTACT_FORM,
      overlay: [],
      payload: {
        queue,
        ...(fromEmail && { fromEmail }),
      },
      text: '',
    };

    replaceNode(nodeOptions);
    triggerClose();
  }, [node, queue, fromEmail, replaceNode, triggerClose, validate]);

  const emailItems = useMemo(
    () =>
      fromEmails.map(({ email_address, sender_name }) => ({
        id: email_address,
        name: sender_name ? `${sender_name} <${email_address}>` : email_address,
      })),
    [fromEmails],
  );

  return (
    <Box width="600px">
      <ModalHead onClose={triggerClose} title={nodeLabel} />
      <form onSubmit={handleSaveClick}>
        <ModalBody>
          <QueuePicker
            required
            selectedQueue={queue}
            label="Select queue"
            placeholder="No queue selected"
            meta={{ error: errors[QUEUE_FIELD] }}
            input={{
              name: QUEUE_FIELD,
              onChange: handleQueueChange,
            }}
          />
          <FieldSelect
            label="Email"
            input={{
              name: EMAIL_FIELD,
              value: fromEmail ?? '',
              onChange: handleEmailChange,
            }}
            items={emailItems}
            placeholder="Select an email"
            clearable
            searchable
          />
        </ModalBody>
        <ModalFoot
          cancelOnClick={triggerClose}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !isChanged }}
          confirmOnClick={handleSaveClick}
        />
      </form>
    </Box>
  );
};

const mapStateToProps = ({
  composeEmail: { fromEmails },
}: RootReducerStateT) => ({
  fromEmails,
});

export const ContactFormModal = connect(mapStateToProps)(ContactFormModalImpl);
